import { useMemo, useState, useEffect } from 'react';
import Joyride, { CallBackProps } from 'react-joyride';

import { useTheme } from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { isAdmin } from 'src/services/auth/auth.utils';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { useGetIncomingJobInvitesQuery } from 'src/services/jobs/jobs.service';
import { showCandidateJobRequestsModal } from 'src/store/slices/jobs/jobsSlice';
import { setShowJoyride, setShowNavMenu } from 'src/store/slices/joyride/joyrideSlice';
import { useUpdateOnboardingContextMutation } from 'src/services/account/account.service';

import { TenantType } from 'src/types/enums';

import AdminSteps from './admin-steps';
import ClientSteps from './client-steps';
import CandidateSteps from './candidate-steps';
import RecruiterSteps from './recruiter-steps';

export default function JoyrideContainer() {
  const router = useRouter();

  const tenantType = useOrgTenant();

  const authContext = useAuthContext();

  const [runKey, setRunKey] = useState(0);

  const [overlayHeight, setOverlayHeight] = useState(document.body.scrollHeight);

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const showWelcomeJoyride = useAppSelector((state) => state.joyride.showWelcomeJoyride);

  const [updateOnboardingStatus] = useUpdateOnboardingContextMutation();

  const [steps, setSteps] = useState<any[]>();

  const isAdminUser = useMemo(() => isAdmin(authContext), [authContext]);

  const { currentData: pendingJobInvites } = useGetIncomingJobInvitesQuery(
    {
      page: 1,
      per_page: 1,
    },
    { skip: tenantType !== TenantType.Candidate }
  );

  useEffect(() => {
    switch (tenantType) {
      case TenantType.Candidate:
        setSteps(CandidateSteps);
        break;
      case TenantType.Recruiter:
        setSteps(isAdminUser ? [...RecruiterSteps, ...AdminSteps] : RecruiterSteps);
        break;
      case TenantType.Client:
        setSteps(isAdminUser ? [...ClientSteps, ...AdminSteps] : ClientSteps);
        break;
      default:
        setSteps(CandidateSteps);
        break;
    }
  }, [isAdminUser, tenantType]);

  const handleCallback = (data: CallBackProps) => {
    const {
      action,
      step: {
        data: { path, nextPath, previousPath, navMenu },
      },
    } = data;
    const isPreviousAction = action === 'prev';

    if (!isPreviousAction) {
      if (path !== nextPath) {
        router.push(nextPath);
      }
    } else if (path !== previousPath) {
      router.push(previousPath);
    }

    if (navMenu) {
      dispatch(setShowNavMenu(true));
    } else {
      dispatch(setShowNavMenu(false));
    }

    setOverlayHeight(document.body.scrollHeight);

    if (action === 'close' || action === 'skip' || action === 'reset') {
      updateOnboardingStatus({
        signup_flow: true,
      });
      dispatch(setShowJoyride(false));
      setRunKey(runKey + 1);

      // show job requests modal if there are any pending job invites
      if (!!pendingJobInvites?.count && tenantType === TenantType.Candidate) {
        dispatch(showCandidateJobRequestsModal());
      }

    }
  };

  return (
    <Joyride
      key={runKey}
      run={showWelcomeJoyride}
      steps={steps}
      continuous
      showProgress
      showSkipButton
      disableScrolling
      callback={handleCallback}
      styles={{
        options: {
          zIndex: 2000,
        },
        overlay: {
          height: overlayHeight,
        },
        // buttonClose: buttonClose,
        buttonNext: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        buttonBack: {
          color: theme.palette.text.primary,
        },
        // buttonSkip: buttonSkip,
        // tooltip: tooltip,
      }}
    />
  );
}
